import { Link } from "react-router-dom";
import { Instagram, Youtube, Disc } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

// Import your logos
import LogoDark from "../assets/noBgBlack.png"; // For Light Mode
import LogoLight from "../assets/noBgWhite.png"; // For Dark Mode

export default function Footer() {
  const { theme } = useTheme();

  // Logic to switch logo based on theme
  const logoSrc = theme === "light" ? LogoDark : LogoLight;

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer
      data-testid="footer"
      className="relative bg-card border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center mb-6">
              <img
                src={logoSrc}
                alt="Edalyze Logo"
                className="h-8 w-auto object-contain transition-all duration-300"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              AI-powered exam evaluation and analytics with board-level accuracy.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              <a
                href="https://www.instagram.com/edalyzeai/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="p-2 rounded-lg bg-accent hover:bg-accent/80 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>

              <a
                href="https://youtube.com/@edalyze"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="p-2 rounded-lg bg-accent hover:bg-accent/80 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-4 h-4" />
              </a>

              <a
                href="https://discord.gg/HmFRAqDw"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-discord"
                className="p-2 rounded-lg bg-accent hover:bg-accent/80 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Discord"
              >
                <Disc className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Product</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection("#capabilities")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Features
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("#how-it-works")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("#pricing")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  API Access
                </button>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Company</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection("#about")}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  About
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Careers
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Blog
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Press
                </button>
              </li>
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Resources</h4>
            <ul className="space-y-3">
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Documentation
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Help Center
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Contact
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Status
                </button>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold text-sm mb-4">Legal</h4>
            <ul className="space-y-3">
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Privacy Policy
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Terms of Service
                </button>
              </li>
              <li>
                <button className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Cookie Policy
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Edalyze. All rights reserved.
          </p>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Built with precision for</span>
            <span className="text-primary font-medium">
              Students • Parents
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
